import { DatelyObject, Employment, EmploymentRelationTaxcard, Employments, Taxcard, TaxCard2019Logic, TaxcardKind, TaxcardListItem, Taxcards, Translations } from "@salaxy/core";

import { EditDialogResult } from "./model";
import type { EmploymentDetailsTabs } from "./model";
import { UiHelpers } from "./UiHelpers";

/** Extends UiHelpers methods with business logic (typically save, load etc.) */
export class UiCrudHelpers {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers", "Taxcards", "Employments"];

  /**
   * Creates a new UiCrudHelpers with dependency injection.
   */
  constructor(
    private uiHelpers: UiHelpers,
    private taxcards: Taxcards,
    private employments: Employments
  ) {
  }

  /**
   * Shows a dialog for editing an existing employment.
   * If user clicks OK, the changes are saved.
   * @param employmentId - Identifier of the Workers employment.
   * @param buttonTypes "default" shows standard save button,
   * "updateCalc" shows two buttons "ok" for full calc update and "ok-no-rows" update without touching default rows.
   * @param initialTab - Tab to open.
   * @returns Dialog result. Note that OK result may be either "ok" or "ok-no-rows".
   * The latter means that for calculation only worker info should be updated, not the default rows.
   */
  public openEditEmploymentDialog(
    employmentId: string,
    buttonTypes: "default" | "updateCalc",
    initialTab: EmploymentDetailsTabs = null,
  ): Promise<EditDialogResult<Employment>> {
    const loader = this.uiHelpers.showLoading("Ladataan työntekijän tietoja...");
    return this.employments.getSingle(employmentId).then((employment) => {
      loader.dismiss();
      return this.uiHelpers.openEditEmploymentDialog(employment, initialTab, buttonTypes).then((result) => {
        if (result.action === "ok" || result.action === "ok-no-rows") {
          const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
          return this.employments.save(result.item).then((savedEmployment) => {
            loader.dismiss();
            result.item = savedEmployment;
            return result;
          });
        } else {
          return result;
        }
      });
    });
  }

  /**
   * Opens a wizard dialog for creating a new Employment relation and potentially a manual Taxcard.
   * If user commits at the end, the changes are saved to both items.
   * @returns Result only has the Employment item (not taxcard - add if necessary).
   */
  public async createNewEmployment(): Promise<EditDialogResult<Employment>> {
    const innerResult = await this.uiHelpers.openNewEmploymentWizard();

    const result: EditDialogResult<Employment> = {
      action: innerResult.action,
      hasChanges: innerResult.hasChanges,
      item: undefined,
      logic: innerResult.logic,
      result: innerResult.result,
    };
    if (innerResult.action === "ok") {
      const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
      result.item = await this.employments.save(innerResult.item.employment);
      const personalId = result.item.identity.personalId;

      switch (result.item.info.taxcard) {
        case EmploymentRelationTaxcard.NoTaxCard:
          // TODO: Should be handled on server-side.
          await this.taxcards.save(TaxCard2019Logic.getBlank(personalId, TaxcardKind.NoTaxCard));
          break;
        case EmploymentRelationTaxcard.Manual: {
          const kind = innerResult.item.taxcard.card.kind;
          if (kind == TaxcardKind.DefaultYearly || kind == TaxcardKind.NoWithholdingHousehold
            || kind == TaxcardKind.Replacement || kind == TaxcardKind.NoTaxCard) {
            await this.taxcards.save(innerResult.item.taxcard);
          }
          break;
        }
        case EmploymentRelationTaxcard.Auto:
          // TODO: May be moved as a separate button in dialog and then assure handled in payment.
          await this.taxcards.setAuto(personalId);
          break;
        case EmploymentRelationTaxcard.TaxAtSource:
        case EmploymentRelationTaxcard.Undefined:
        default:
          // No taxcard defintion OR info set in employment.
          break;
      }
      loader.dismiss();
    }
    return result;
  }

  /**
   * Shows the add new taxcard dialog.
   * If user commits at the end, the changes are saved and the ite is the stored taxcard from server.
   * @param personalId Worker personal id is required.
   */
  public createNewTaxcard(personalId: string): Promise<EditDialogResult<Taxcard>> {
    return this.uiHelpers.openNewTaxcardDialog(personalId)
      .then((result) => {
        if (result.action === "ok") {
          const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
          return this.taxcards.save(result.item).then((savedTaxcard) => {
            result.item = savedTaxcard;
            loader.dismiss();
            if (!savedTaxcard) {
              this.uiHelpers.showAlert(Translations.get("SALAXY.NG1_Services.UiCrudHelpers.taxcards.noTaxcardTitle"),
                Translations.get("SALAXY.NG1_Services.UiCrudHelpers.taxcards.noTaxcardText"));
            }
            if (savedTaxcard?.incomeLog.find((x) => x.diff && x.diff !== "default")) {
              return this.uiHelpers.openEditDialog("salaxy-components/modals/worker/taxcard-income-log.html", savedTaxcard, {}, "lg")
                .then((incomeLogResult) => {
                  if (incomeLogResult.action === "ok") {
                    const loader = this.uiHelpers.showLoading("Tallennetaan laskelmia...");
                    return this.taxcards.commitDiff(savedTaxcard).then((commitResult) => {
                      loader.dismiss();
                      result.item = commitResult;
                      return result;
                    });
                  } else {
                    return result;
                  }
                });
            }
            return result;
          });
        } else {
          return result;
        }
      });
  }

  /**
   * Refreshes the automatic taxcard.
   * @param taxcardId Automatic taxcard id to refresh.
   * @param salaryDate Salary date for the taxcard.
   */
  public refreshAutoTaxcard(taxcardId: string, salaryDate?: DatelyObject): Promise<TaxcardListItem[]> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait", "Verokorttia tarkistetaan.");
    return this.taxcards.refreshAuto(taxcardId, salaryDate, true).then((taxcards) => {
      loading.dismiss();
      return taxcards;
    });
  }
}
